/*
  Filters the product gallery to one colourway.

  Colour is not a variant here, so there is no variant change event to listen
  to. The gallery is filtered directly: every slide names its colour in the
  image alt text, written by the asset pipeline, so the colour of a slide can
  be read off the slide itself. Dawn's gallery markup is untouched.

  Without JavaScript every image stays visible, which is the honest fallback:
  all the product's images, just not filtered.
*/
class ColourPicker extends HTMLElement {
  connectedCallback() {
    this.colourways = (this.dataset.colourways || '').split('|').filter(Boolean);
    if (this.colourways.length < 2) return;

    this.label = this.querySelector('[data-colour-label]');
    this.status = this.querySelector('[data-colour-status]');
    this.gallery = document.querySelector('media-gallery') || document;

    this.slides = this._collectSlides();
    if (!this.slides.length) return;

    this.addEventListener('change', (event) => {
      if (event.target.matches('.momani-option__input')) this.select(event.target.value);
    });

    document.documentElement.classList.add('momani-colours-active');
    this.select(this.querySelector('.momani-option__input:checked')?.value || this.colourways[0]);
  }

  /** Every gallery slide, paired with the colourway its alt text names. */
  _collectSlides() {
    const nodes = this.gallery.querySelectorAll(
      '.product__media-item, .thumbnail-list__item',
    );
    return [...nodes].map((node) => {
      const alt = node.querySelector('img')?.getAttribute('alt') || '';
      const colour = this.colourways.find((c) => alt.includes(` in ${c},`)) || null;
      return { node, colour };
    });
  }

  select(colour) {
    if (!this.colourways.includes(colour)) return;
    this.current = colour;

    let shown = 0;
    let first = null;
    this.slides.forEach(({ node, colour: own }) => {
      // A slide whose colour cannot be read stays visible. Hiding it would
      // silently drop an image the merchant added by hand.
      const show = own === null || own === colour;
      node.hidden = !show;
      node.classList.toggle('momani-colour-hidden', !show);
      if (show && own === colour) {
        shown += 1;
        if (!first) first = node;
      }
    });

    if (this.label) this.label.textContent = colour;
    if (this.status) this.status.textContent = `Showing ${shown} images in ${colour}.`;

    // Bring the first image of the chosen colour into view, without scrolling
    // the page for a customer who has not asked to move.
    if (first && this.gallery.setActiveMedia) {
      const id = first.dataset.mediaId;
      if (id) this.gallery.setActiveMedia(id, false);
    } else if (first) {
      const slider = first.closest('.slider');
      if (slider) slider.scrollTo({ left: first.offsetLeft - slider.offsetLeft, behavior: 'instant' });
    }

    this.dispatchEvent(new CustomEvent('momani:colour', {
      bubbles: true, detail: { colour, shown },
    }));
  }
}

if (!customElements.get('colour-picker')) {
  customElements.define('colour-picker', ColourPicker);
}
